import { ConditionViewerItem, ConditionViewerTerm, SkeletonText } from '@customerio/pluma-components/react';
import { useQuery } from '@tanstack/react-query';
import {
  getOptOutIdentities,
  normalizeOptOutChannel,
  optOutChannelLabel,
  type OptOutChannel,
} from '@/features/condition-builder';
import { useSenderLabel, type SenderLabel } from '@/features/sender-identities';
import { getEnvironmentQueryOptions } from '@/services/db/environment/queries';
import { isEmptyString } from '@/utils/string';
import type { OptOutNode } from '../../types';
import { getIconForNode } from '../data-source-icon';

/** Where the sender's name will be, about as wide as one. */
const SKELETON_STYLE = { width: '8em', display: 'inline-block', verticalAlign: 'middle' };

function useConditionSender(node: OptOutNode, workspaceId: number): SenderLabel | undefined {
  const sender = node.sender ?? '';
  const channel = normalizeOptOutChannel(node.channel);
  const environment = useQuery({
    ...getEnvironmentQueryOptions(workspaceId),
    enabled: workspaceId > 0 && sender !== '',
  });
  const label = useSenderLabel({
    workspaceId,
    sender,
    name: channelSenderName(sender, channel, environment.data?.identities),
    isNameLoading: environment.isLoading,
  });
  return sender === '' ? undefined : label;
}

function channelSenderName(
  sender: string,
  channel: OptOutChannel,
  identities: Parameters<typeof getOptOutIdentities>[0] = [],
): string | undefined {
  const lower = sender.toLowerCase();
  const name = getOptOutIdentities(identities, channel).find(
    (identity) => identity.phone.toLowerCase() === lower,
  )?.name;
  return isEmptyString(name) ? undefined : name;
}

type SenderTermProps = { sender: SenderLabel };

function SenderTerm({ sender }: SenderTermProps) {
  if (sender.isLoading) {
    return <SkeletonText as="span" style={SKELETON_STYLE} aria-hidden="true" data-testid="opt-out-sender-skeleton" />;
  }
  return <ConditionViewerTerm>{sender.label}</ConditionViewerTerm>;
}

type OptOutConditionProps = { node: OptOutNode; workspaceId: number };

/**
 * `User is opted out of SMS from <sender>`, or `… from any sender` when the
 * condition names none. A condition with no channel is an SMS opt-out.
 */
export function OptOutCondition({ node, workspaceId }: OptOutConditionProps) {
  const sender = useConditionSender(node, workspaceId);
  const operator = node.inverse ? 'User is not opted out of' : 'User is opted out of';
  const prefix = `${operator} ${optOutChannelLabel(node.channel)}`;
  return (
    <ConditionViewerItem icon={getIconForNode(node)}>
      {sender == null ? (
        `${prefix} from any sender`
      ) : (
        <>
          {prefix} from <SenderTerm sender={sender} />
        </>
      )}
    </ConditionViewerItem>
  );
}
